import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { useBenchHandoff } from "../../../hooks/useBenchHandoff";
import { boardKind } from "../../../lib/api/fieldApi";
import { PLAYGROUND_MODE, useMissionApi } from "../../../lib/api/missionApi";
import type { Mission } from "../../../lib/api/missionApi";
import type { BenchSource } from "../../../lib/api/fieldApi";
import { useI18n } from "../../../i18n";
import { useMicrobitLive } from "../hooks/useMicrobitLive";
import type { LiveWindow } from "../hooks/useMicrobitLive";
import { isBlank, toRemote } from "../lib/live";
import { GRID, ICONS_GOAL, STARS_GOAL, goalsOf, initialPlay, reactionOf, stepPlay } from "../lib/play";
import type { PlayState } from "../lib/play";
import { BEATS_GOAL, SCENES, TRACK_GOAL, initialFaceCourse, initialHeartbeat, initialMovie, initialTrack, litLevel, moviePlaying, radioLessonOf, sceneOf, stepFaceCourse, stepHeartbeat, stepMovie, stepTrack } from "../lib/scenes";
import type { FaceCourse, Heartbeat, Movie, SceneId, Track } from "../lib/scenes";
import { FaceCoursePanel, HeartbeatPanel, MoviePanel, TrackPanel } from "./ScenePanels";
import { SimPane } from "./SimPane";
import { BoardPanel } from "./BoardPanel";
import { BoardMirror, GatewayMirror } from "./BoardMirror";
import { GATEWAY_STALE_MS, parseGatewayLine, txLine } from "../lib/gateway";
import { useBoard } from "../hooks/useBoard";
import { readConfig } from "../../../lib/config";
import { useDensity } from "../../../store/density";
import { frameLessons, initialLessons, tickLessons } from "../lib/lessons";
import { useMbRadio } from "../hooks/useMbRadio";
import { useRadioApi } from "../api/radioApi";
import { RadioPanel } from "./RadioPanel";
import { SensorPanel } from "./SensorPanel";
import { BalancePanel, DoorPanel, MarathonPanel, MazePanel, WatchPanel } from "./RemotePanels";
import { BandPanel, CheerPanel, HotColdPanel, MelodyPanel, ShowcasePanel } from "./StagePanels";
import { HOT_PING_MS, numberPacket, rssiOf } from "../lib/stages";
import { initialQuest, questCard, questCards, questDone, questOf, questTick } from "../lib/aiQuest";
import { doneLessonsOf, missionSceneLesson } from "../lib/sceneDone";
import type { QuestState } from "../lib/aiQuest";
import { initialSensor, sceneOfLesson, stepSensor } from "../lib/sensorWorld";
import type { SensorState } from "../lib/sensorWorld";
import { AiCardsPanel } from "./AiCardsPanel";
import { exchanged, partnerView } from "../lib/radio";
import { useAiBridge } from "../hooks/useAiBridge";
import { glyphOf } from "../lib/font";
import type { Lessons } from "../lib/lessons";
import { isLevelCheck } from "../lib/games";
import {
  BallMazePanel, BoardGamePanel, DicePanel, GalleryPanel, GatePanel, GoalsPanel, GreetPanel, HarborPanel, LevelPanel, MarqueePanel, NameTagPanel,
  QuizPanel, ReactionPanel, RpsPanel, StoryPanel,
} from "./LessonPanels";
import { MbStage } from "./MbStage";
import { readMbGoals, sceneStars, starTargets } from "../lib/sceneStars";
import type { MbGoalConfig } from "../lib/sceneStars";
import { readStageDecor, readStageModels, stageModelUrlFor } from "../../stage";
import type { StageModelEntry } from "../../stage";
import { useCourseApi } from "../../../lib/api/courseApi";
import { ambienceOf, templateOf } from "../lib/stageTemplate";
import type { StageGoalLine } from "../../stage";
import { mbDecorKey, mbKey, readStage3d, writeStage3d } from "../lib/stage3d";
import { cleanDefaultDecor } from "../lib/stagePreview";
import "../../../styles/microbit.css";

/** 놀이 고르기 묶음 — 차시 번호로 나눈다(라디오 광장은 2단계) */
/** 장면은 놀이를 시작하고 이만큼 뒤에 끝나야 증거다(서버 PlaygroundMissionService.MIN_SCENE_MS) */
const SCENE_MIN_MS = 3000;
/** 시뮬레이터가 처음 온 뒤 카드를 받기까지(Bench 시뮬레이터만 보기 재시도 0.3 · 1.5 · 4 · 8초를 넘게) */
const WAKE_MS = 9000;
/** Bench 가 시뮬레이터가 섰다고 알린 뒤 카드를 열기까지(프로그램이 처음 한 줄을 그릴 틈) */
const READY_GRACE_MS = 800;
/** Bench → Field 시뮬레이터가 섰다(Bench field-live FIELD_SIM_READY_TYPE 와 같다) */
const SIM_READY_TYPE = "bench.mbSimReady";
const SCENE_GROUPS = ["common", "low", "lesson", "sensor", "radio", "ai"] as const;
const groupOf = (sc: (typeof SCENES)[number]): (typeof SCENE_GROUPS)[number] =>
  sc.id === "plaza" ? "radio" : sc.id === "aiCards" ? "ai" : sceneOfLesson(sc.id) ? "sensor" : !sc.lesson ? "common" : sc.lesson.startsWith("LE") ? "low" : "lesson";

/** 소식이 이만큼 없으면 '연결 끊김' */
export const STALE_MS = 2000;
const TICK_MS = 100;

/**
 * FS-MB 마이크로비트 놀이터(1단계 — PICNIC-MB-FIELD-001 v0.2 §6 1단계: 통로 ① 화면 읽기 · ② 입력 따라하기).
 *  - 왼쪽: 내 micro:bit 작품을 돌리는 MakeCode 시뮬레이터(SimPane — Bench 편집기 '시뮬레이터만 보기' · 1회용 입장표 · iframe · 재생 전용)
 *    · 연결 상태 · 지금 아이콘 · 입력(A · B · 로고 · 흔들기 · 기울기). 고치기는 'Bench 에서 고치기'(새 탭) → [다시 불러오기].
 *    시뮬레이터의 화면 · 입력은 Bench 가 놀이터 연결 확장으로 이 창에 넘긴다(lib/live) — 왼쪽 칸이 있으면 그 칸에서 온 것만 받는다. 오른쪽: 놀이 고르기(lib/scenes — 별 모으기 · 차시별 장면 LE01~05 · L01~L14 ·
 *    주소 `?play=L06` 로 미리 고름) · 무대(아이콘에 관객 반응). 장면들은 같은 보드 소식으로 함께 돌고(바꿔도 진행이 남는다) 고른 것만 그린다.
 *    둘째 묶음(lib/lessons)은 놀이 시계로 한 걸음 · 흐르는 글자 · 출발 시각은 소식마다(useMicrobitLive onFrame).
 *  - 라디오 광장(2단계 · 통로 ③ — hooks/useMbRadio): 같은 반 친구들의 시뮬레이터 라디오를 룸 서버 `mbradio` 로 잇는다.
 *  - 진짜 보드(4단계 — hooks/useBoard · BoardPanel): Web Serial 로 꽂은 보드의 PF1 줄이 시뮬레이터 대신 놀이터를 움직이고, AI 값은 보드로 한 줄.
 *  - 미션 셋(lib/play goalsOf) — 놀이터가 본 사건으로 판정.
 *  - '오늘의 미션'(Campus 티켓에 모드 `microbit-playground` 목표가 있을 때) — 놀이에서 그 목표를 채우면 본 것(아이콘 · 버튼 · 별 · 시간)을 제어 평면에
 *    보내고(POST …/mission/playground — 서버가 같은 규칙으로 다시 따져 첫 달성 · Campus 로 missionResult observed), 서버 상태(확인됨)를 보인다.
 */
export function MicrobitScreen({ win }: { win?: LiveWindow } = {}) {
  const { t, locale } = useI18n();
  // 운영 콘솔이 배포한 마이크로비트 무대 설정(2026-10-08) — 별 둘 · 셋 기준(mb_goal) · 무대 꾸밈(stage_scene 'mb-<장면>') · 올린 3D 모델
  const courseApi = useCourseApi();
  const [mbGoals, setMbGoals] = useState<ReadonlyMap<string, MbGoalConfig>>(new Map());
  const [mbDecor, setMbDecor] = useState<ReadonlyMap<string, unknown>>(new Map());
  const [mbModels, setMbModels] = useState<ReadonlyMap<string, StageModelEntry>>(new Map());
  useEffect(() => {
    if (!courseApi) return;
    let alive = true;
    void Promise.all([
      courseApi.catalog("mb_goal").then(readMbGoals, () => new Map<string, MbGoalConfig>()),
      courseApi.catalog("stage_scene").then(readStageDecor, () => new Map<string, unknown>()),
      courseApi.catalog("stage_model").then(readStageModels, () => new Map<string, StageModelEntry>()),
    ]).then(([g, d, m]) => {
      if (!alive) return;
      setMbGoals(g);
      setMbDecor(d);
      setMbModels(m);
    });
    return () => { alive = false; };
  }, [courseApi]);
  const mbModelUrl = useMemo(
    () => (courseApi && mbModels.size ? stageModelUrlFor(mbModels, (sha) => courseApi.stageModel(sha)) : null),
    [courseApi, mbModels],
  );
  const api = useFieldApi();
  const [lessons, setLessons] = useState<Lessons>(initialLessons);
  const benchOrigin = readConfig().benchOrigin;
  /** 왼쪽 칸(시뮬레이터)의 창 — 있으면 그 칸에서 온 소식만 받는다('Bench 에서 고치기' 탭의 시뮬레이터가 섞이지 않게) */
  const simWin = useRef<Window | null>(null);
  const boardOn = useRef(false);
  const [now, setNow] = useState(() => Date.now());
  // 진짜 보드가 연결된 동안은 칸이 내려가 있다(보드 거울) — 어느 창의 시뮬레이터 소식도 받지 않는다(고치기 탭이 섞이지 않게)
  const fromSim = (ev: MessageEvent) => !boardOn.current && (!simWin.current || ev.source === simWin.current);
  /** 놀이터 칸 시뮬레이터가 섰다(Bench bench.mbSimReady — 시작 명령이 받아들여졌다) — 그때부터 카드를 연다. 칸을 다시 불러오면 지운다 */
  const [simReadyAt, setSimReadyAt] = useState<number | null>(null);
  useEffect(() => {
    if (!benchOrigin) return;
    const h = (ev: MessageEvent) => {
      if (ev.origin !== benchOrigin || !fromSim(ev)) return;
      const d = ev.data as { type?: unknown; v?: unknown } | null;
      if (d?.type === SIM_READY_TYPE && d.v === 1) setSimReadyAt(Date.now());
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fromSim 은 ref 만 읽는다
  }, [benchOrigin]);
  // 진짜 보드(4단계) — 연결되면 보드의 PF1 줄이 시뮬레이터 소식 대신 놀이터를 움직인다
  // 라디오 광장 게이트웨이(§6.7 — lib/gateway): 꽂은 보드가 'GW1' 을 보내면 게이트웨이다 — 공중 ↔ 광장을 잇는다(놀이 보드가 아니다)
  const [gw, setGw] = useState<{ g: number; at: number; up: number; down: number } | null>(null);
  const gwRef = useRef(gw);
  gwRef.current = gw;
  const onBoardLine = (line: string) => {
    const m = parseGatewayLine(line);
    if (!m) return;
    if (m.kind === "hello") { setGw((x) => ({ g: m.g, at: Date.now(), up: x?.up ?? 0, down: x?.down ?? 0 })); return; }
    const cur = gwRef.current;
    if (!cur) return;
    radioRef.current?.sendAir(cur.g, m.p);
    setGw((x) => (x ? { ...x, up: x.up + 1 } : x));
  };
  // 흔들기는 순간이다 — 시뮬레이터 확장은 흔든 뒤 첫 줄에만 S=1 을 싣고 바로 다음 줄은 S=0 이라, 0.1초 놀이 시계가 그 줄을 건너뛸 수 있다.
  // 소식이 올 때마다 흔들기를 붙잡아 두었다가 다음 시계 한 걸음에 한 번 넘긴다(2026-10-05 운영 실측 — 흔든 4번 중 2번만 셌다)
  const shakeLatch = useRef(false);
  const board = useBoard((v, ts) => { if (v.frame.shake) shakeLatch.current = true; setLessons((l) => frameLessons(l, ts, v.frame)); }, { onLine: onBoardLine });
  boardOn.current = board.on;
  const sim = useMicrobitLive(win, undefined, (v, ts) => {
    if (boardOn.current) return;
    if (v.frame.shake) shakeLatch.current = true;
    setLessons((l) => frameLessons(l, ts, v.frame));
  }, fromSim);
  const live = board.on ? board.live : sim.live;
  const at = board.on ? board.at : sim.at;
  const handoff = useBenchHandoff(api, () => {});
  const [projects, setProjects] = useState<BenchSource[] | null>(null);
  const [play, setPlay] = useState<PlayState>(initialPlay);
  const [stage3d, setStage3d] = useState(readStage3d);
  const [stage3dFailed, setStage3dFailed] = useState(false);
  const [params, setParams] = useSearchParams();
  const missionApi = useMissionApi();
  const [mission, setMission] = useState<Mission | null>(null);
  // 장면 — 주소(?play=) > 오늘의 미션의 차시 장면(scene:LE04) > 지금 보드가 보내는 작품의 차시(Bench lesson_meta · 예: MB-L06) > 내 micro:bit 작품이 하나면 그 차시 > 별 모으기
  // 미션 장면이 먼저인 까닭(2026-10-06 운영 실측): 4회차에서 놀이터를 열어도 아이 작품이 1회차 것이면 1회차 장면으로 열렸다 — 미션은 그 회차 장면을 바란다
  const missionLesson = missionSceneLesson(mission);
  /** 오늘의 미션 차시의 작품(있으면 놀이터 칸이 그것을 싣는다) — 없으면 아이가 아직 그 회차 코딩을 열지 않았다 */
  const missionProject = missionLesson ? projects?.find((p) => (p.lessonId ?? "").replace(/^MB-/i, "").toUpperCase() === missionLesson) : undefined;
  const lessonProject = projects?.find((p) => p.projectId === live?.projectId) ?? missionProject ?? (projects?.length === 1 ? projects[0] : undefined);
  const autoScene = sceneOf(lessonProject?.lessonId);
  const scene: SceneId = sceneOf(params.get("play")) ?? sceneOf(missionLesson) ?? autoScene ?? "stars";
  // 라디오 광장(2단계) — 광장을 고르거나 내 시뮬레이터가 라디오를 보내면 붙는다
  const radioApi = useRadioApi();
  const gatewayOn = board.on && gw !== null && now - gw.at < GATEWAY_STALE_MS;
  /** 광장 · 내 시뮬레이터 패킷 → 게이트웨이 보드 → 공중(그 보드의 그룹만 · 공중에서 온 것은 다시 보내지 않는다 — 되먹임 없음) */
  const tap = (m: { g: number; p: number[]; air: boolean }) => {
    const cur = gwRef.current;
    if (!cur || !boardOn.current || m.air || m.g !== cur.g) return;
    if (board.send(txLine(m.p))) setGw((x) => (x ? { ...x, down: x.down + 1 } : x));
  };
  const radio = useMbRadio(radioApi, scene === "plaza" || gatewayOn, { accept: fromSim, tap, ...(win ? { win } : {}) });
  const radioRef = useRef(radio);
  radioRef.current = radio;
  // AI 카드(3단계) — 칸(Bench)이 알려 주는 프로그램의 클래스 이름 · 카메라/카드가 고른 클래스를 칸으로
  const ai = useAiBridge(simWin, win ? { win } : {});
  const { density } = useDensity();
  // 센서 월드(5단계) — 고른 센서 장면만 돈다 · 그 장면의 센서를 시뮬레이터 칸에 2초마다 요청한다(칸이 다시 시작해도 곧 다시 선다)
  const sensorScene = sceneOfLesson(scene);
  const [sensorStates, setSensorStates] = useState<Record<string, SensorState>>({});
  const sensorRef = useRef(sensorScene);
  sensorRef.current = sensorScene;
  // 운동장 보물찾기(X09 · lib/stages) — 놀이터가 보물 보드: 0.5초마다 아이 프로그램의 라디오 그룹으로 숫자 1 을 시뮬레이터에 넣는다.
  // 신호 세기는 캐릭터와 보물의 거리(가까울수록 크다) — 아이의 '라디오 수신하면 · 신호 세기'가 받는다(Bench field.mbRadio rssi)
  const hotRef = useRef(lessons.stages.hotCold);
  hotRef.current = lessons.stages.hotCold;
  useEffect(() => {
    if (scene !== "hotCold" || board.on) return;
    let t = 0;
    const h = window.setInterval(() => {
      t += HOT_PING_MS;
      try { simWin.current?.postMessage({ type: "field.mbRadio", v: 1, g: ai.radioGroup ?? 0, p: numberPacket(1, t), rssi: rssiOf(hotRef.current) }, benchOrigin ?? ""); } catch { /* 칸이 아직 없다 */ }
    }, HOT_PING_MS);
    return () => window.clearInterval(h);
  }, [scene, board.on, ai.radioGroup, benchOrigin]);
  // AI 차시 놀이(lib/aiQuest — §6.12): 주소(?play=AI05)가 먼저, 아니면 내 작품의 차시(MB-AI05) — 둘 다 아니면 자유 카드 놀이
  const quest = scene === "aiCards" ? questOf(params.get("play")) ?? questOf(missionLesson) ?? questOf(lessonProject?.lessonId) : null;
  useEffect(() => {
    // 손목시계(L33)는 '따뜻한 곳 찾기'에 온도가 필요하다
    // 창문 관리인(AI29)은 '추워지면'에 온도가 필요하다
    const want = sensorScene ? [...sensorScene.sensors] : scene === "watch" || (scene === "aiCards" && quest?.cold) ? ["temp"] : [];
    const post = () => { try { simWin.current?.postMessage({ type: "field.mbSensors", v: 1, sensors: want }, benchOrigin ?? ""); } catch { /* 칸이 아직 없다 */ } };
    post();
    if (!want.length) return;
    const h = window.setInterval(post, 2000);
    return () => window.clearInterval(h);
  }, [sensorScene, scene, benchOrigin, quest?.cold]);
  const qCards = quest ? questCards(quest, ai.classes) : [];
  const [questStates, setQuestStates] = useState<Record<string, QuestState>>({});
  const questRef = useRef({ quest, qCards });
  questRef.current = { quest, qCards };
  const onQuestCard = (card: string) => {
    if (!quest) return;
    const sent = radioRef.current.plaza.sent;
    setQuestStates((m) => ({ ...m, [quest.id]: questCard(quest, qCards, m[quest.id] ?? initialQuest(), card, Date.now(), frameRef.current, sent) }));
  };
  const pickQuest = (id: string | null) => setParams((p) => { const n = new URLSearchParams(p); n.set("play", id ?? "aiCards"); return n; }, { replace: true });
  /** AI 값 — 진짜 보드면 USB 시리얼 한 줄(피크닉 AI 확장이 받는다), 아니면 왼쪽 칸(시뮬레이터) */
  const sendAi = (klass: string, confidence: number, via: "camera" | "card") => {
    if (!board.on) return ai.send(klass, confidence, via);
    const k = klass.replace(/[\r\n:]/g, " ").trim();
    return k ? board.send(`AI:${k}:${Math.round(confidence)}`) : false;
  };
  const [course, setCourse] = useState<FaceCourse>(initialFaceCourse);
  const [track, setTrack] = useState<Track>(initialTrack);
  const [heart, setHeart] = useState<Heartbeat>(initialHeartbeat);
  const [movie, setMovie] = useState<Movie>(initialMovie);
  const frameRef = useRef(live?.frame ?? null);
  frameRef.current = live?.frame ?? null;
  const [sending, setSending] = useState(false);
  /** 이미 보낸 프리셋(같은 놀이에서 다시 보내지 않는다 — 서버도 첫 한 번만) */
  const sentRef = useRef(new Set<string>());
  /** 놀이 시작 시각(처음 연결된 때) — 증거의 지난 시간 */
  const startRef = useRef<number | null>(null);
  useEffect(() => { if (at !== null && startRef.current === null) startRef.current = at; }, [at]);

  useTopBar({ icon: "🟥", title: t("student.microbit.title") });

  useEffect(() => {
    if (!api) return;
    let alive = true;
    api.sources().then(
      (s) => alive && setProjects(s.bench.filter((b) => boardKind(b.board) === "microbit")),
      () => alive && setProjects([]),
    );
    return () => { alive = false; };
  }, [api]);

  useEffect(() => {
    if (!missionApi) return;
    let alive = true;
    void missionApi.mission().then((m) => alive && setMission(m));
    return () => { alive = false; };
  }, [missionApi]);

  // 오늘의 미션 — 놀이에서 채운 놀이터 목표가 아직 열려 있으면 본 것을 보낸다(프리셋마다 한 번)
  const pgGoals = (mission?.goals ?? []).filter((g) => g.modeId === PLAYGROUND_MODE);
  // 놀이터 목표 = 기본 셋(아이콘 · 버튼 · 별) + 차시 장면 넷(코스 · 걸음 · 박동 · 영화 — Campus '필드: 놀이터 코스|걸음|박동|영화')
  const local = { ...goalsOf(play), course: course.done, steps: track.steps >= TRACK_GOAL, beats: heart.beats >= BEATS_GOAL, movie: moviePlaying(movie) };
  // 차시 장면 해내기(§6.13 · 결정 '나') — 장면마다 처음 해낸 때(놀이 시작부터 ms)를 적는다. 프리셋 `scene:<차시>` 목표는 이것으로 채운다
  // 지금 연 장면만(lib/sceneDone — 다른 장면에서 놀다가 저절로 채워진 것은 세지 않는다)
  const radioLesson = radioLessonOf(params.get("play")) ?? radioLessonOf(missionLesson) ?? radioLessonOf(lessonProject?.lessonId);
  const qst = quest ? questStates[quest.id] : undefined;
  const doneLessons = doneLessonsOf({
    scene, local: { movie: local.movie, heartbeat: local.beats, faceCourse: local.course, track: local.steps }, lessons,
    sensorDone: (id) => !!sensorStates[id]?.done,
    quest: quest ? { id: quest.id, done: !!qst && questDone(quest, qCards, qst) } : null,
    radio: { lesson: radioLesson, exchanged: exchanged(radio.plaza) },
  });
  const doneKey = doneLessons.join(",");
  const [sceneDoneAt, setSceneDoneAt] = useState<Record<string, number>>({});
  const [sceneWake, setSceneWake] = useState(0);
  useEffect(() => {
    if (!doneKey || startRef.current === null) return;
    const now = Date.now() - startRef.current;
    // 시작 3초 안에 끝난 장면은 3초가 지나도 여전히 끝나 있으면 그때 적는다(서버 MIN_SCENE_MS — 적힌 때는 늘 실제로 본 때)
    if (now < SCENE_MIN_MS) { const id = setTimeout(() => setSceneWake((n) => n + 1), SCENE_MIN_MS - now + 50); return () => clearTimeout(id); }
    setSceneDoneAt((m) => {
      const add = doneKey.split(",").filter((l) => m[l] === undefined);
      return add.length ? { ...m, ...Object.fromEntries(add.map((l) => [l, now])) } : m;
    });
  }, [doneKey, sceneWake]);
  /** 서버가 받는 장면(놀이 시작 3초 뒤에 본 것 · 서버 MIN_SCENE_MS) */
  const reportable = Object.entries(sceneDoneAt).filter(([, at]) => at >= SCENE_MIN_MS);
  const sceneOk = (key: string) => key.startsWith("scene:") && reportable.some(([l]) => l === key.slice(6));
  const want = pgGoals.filter((g) => g.state !== "verified" && (local[g.presetKey as keyof typeof local] === true || sceneOk(g.presetKey)) && !sentRef.current.has(g.presetKey));
  const wantKey = want.map((g) => g.presetKey).join(",");
  useEffect(() => {
    if (!missionApi || !wantKey) return;
    for (const k of wantKey.split(",")) sentRef.current.add(k);
    setSending(true);
    const elapsedMs = Math.max(0, Math.min(3_600_000, Date.now() - (startRef.current ?? Date.now())));
    void missionApi
      .playground({ icons: [...play.icons], pressedA: play.pressedA, pressedB: play.pressedB, stars: play.stars, elapsedMs,
        courseDone: course.done, steps: track.steps, beats: heart.beats, movieFrames: movie.loop,
        scenes: reportable.filter(([, at]) => at <= elapsedMs).slice(0, 128).map(([lesson, at]) => ({ lesson, doneAtMs: at, n: 1, goal: 1 })) })
      .then(() => missionApi.mission())
      .then((m) => setMission(m))
      .catch(() => { for (const k of wantKey.split(",")) sentRef.current.delete(k); })
      .finally(() => setSending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 보낼 목표가 바뀔 때만(놀이 상태는 그때의 것을 싣는다)
  }, [missionApi, wantKey]);

  // 놀이 시계 — 0.1초마다 한 걸음(보드 소식이 끊기면 입력 없음으로)
  useEffect(() => {
    const h = window.setInterval(() => {
      const ts = Date.now();
      setNow(ts);
      const f = frameRef.current;
      const latched = shakeLatch.current;
      shakeLatch.current = false;
      const live0 = f && at !== null && ts - at < STALE_MS ? f : null;
      const fresh = live0 && latched && !live0.shake ? { ...live0, shake: true } : live0;
      setPlay((p) => stepPlay(p, TICK_MS, toRemote(fresh), fresh));
      setCourse((c) => stepFaceCourse(c, TICK_MS, fresh));
      setTrack((c) => stepTrack(c, fresh));
      setHeart((c) => stepHeartbeat(c, TICK_MS, fresh));
      setMovie((c) => stepMovie(c, fresh));
      setLessons((l) => tickLessons(l, TICK_MS, fresh));
      const qr = questRef.current;
      if (qr.quest) {
        const qq = qr.quest, cs = qr.qCards, sent = radioRef.current.plaza.sent;
        setQuestStates((m) => ({ ...m, [qq.id]: questTick(qq, cs, m[qq.id] ?? initialQuest(), ts, fresh, sent) }));
      }
      const sc = sensorRef.current;
      if (sc) setSensorStates((m) => ({ ...m, [sc.id]: stepSensor(sc, m[sc.id] ?? initialSensor(), TICK_MS, fresh) }));
    }, TICK_MS);
    return () => window.clearInterval(h);
  }, [at]);

  const connected = live !== null && at !== null && now - at < STALE_MS;
  const frame = connected ? live!.frame : null;
  // 3D 무대(2026-10-08 — 기구 무대와 같은 무대 · 별) — 별 하나 = 지금 장면 해냄(차시 장면) · 별 모으기는 별 목표
  const star1 = doneLessons.length > 0 || (scene === "stars" && local.stars);
  // 별 둘 · 셋(lib/sceneStars — 승인된 기준표) — 끝이 있는 장면은 장면을 연 뒤 해낸 때까지 시간 · 한 번 켜진 별은 그 장면에서 꺼지지 않는다
  const sceneClock = useRef<{ scene: string; openAt: number; already: boolean; doneMs: number | null }>({ scene: "", openAt: 0, already: false, doneMs: null });
  if (sceneClock.current.scene !== scene) sceneClock.current = { scene, openAt: Date.now(), already: star1, doneMs: null };
  if (star1 && !sceneClock.current.already && sceneClock.current.doneMs === null) sceneClock.current.doneMs = Date.now() - sceneClock.current.openAt;
  const goalCfg = mbGoals.get(mbKey(scene)) ?? null;
  const st = sceneStars({ scene, star1, lessons, play, track, heart, movie, plaza: radio.plaza, doneMs: sceneClock.current.doneMs }, goalCfg);
  const bestStars = useRef<Record<string, number>>({});
  const stars = Math.max(st.stars, bestStars.current[scene] ?? 0);
  bestStars.current[scene] = stars;
  const starGoals: StageGoalLine[] = [
    { text: t(`student.microbit.scene.${scene}.name`), done: star1 },
    ...st.lines.map((l, i) => ({ text: (locale === "en" ? l.label?.en : l.label?.ko)?.trim() || t(l.key, l.params), done: l.done || stars >= i + 2, ...(l.fill !== undefined ? { fill: l.fill } : {}), ...(l.num ? { num: l.num } : {}) })),
  ];
  const goals = local;
  const icon = play.lastIcon;
  const iconName = icon ? t(`student.microbit.icon.${icon}`) : null;
  // 보드에 지금 보이는 것 — 아이콘 이름 · 숫자 · 글자 · 내 그림(AI 카드의 '보드 반응'도 이것)
  const glyph = frame && !iconName ? glyphOf(frame.leds) : null;
  /**
   * 보드가 깨는 중 — 시뮬레이터가 아직 놀이터에 오지 않았거나 처음 온 뒤 WAKE_MS 안. 그동안 Bench 가 '시뮬레이터만 보기'를 몇 번 다시 보내고(0.3~8초)
   * 그때 시뮬레이터가 다시 시작하면 보낸 카드를 놓친다(2026-10-06 운영 실측: 놀이터를 연 직후 카드에 '보드가 반응하지 않았어요' — 아이는 프로그램 탓으로 안다)
   */
  const waking: "off" | "waking" | null = board.on ? null : !connected ? "off"
    : simReadyAt !== null ? (now - simReadyAt < READY_GRACE_MS ? "waking" : null)   // Bench 가 '섰다'를 알렸다 — 잠깐만
    : startRef.current !== null && now - startRef.current < WAKE_MS ? "waking" : null; // 옛 Bench(알림 없음) — 9초
  const boardLabel = iconName ?? (glyph ? (/[0-9]/.test(glyph) ? t("student.microbit.digit", { n: glyph }) : t("student.microbit.letter", { c: glyph }))
    : frame && !isBlank(frame.leds) ? t("student.microbit.myPicture") : "");

  return (
    <ScreenRoot id="FS-MB" title={t("student.microbit.title")} hideTitle>
      <div className="pf-mb">
        <section className="pf-mb__board" aria-label={t("student.microbit.board")}>
          <SimPane api={api} benchOrigin={benchOrigin} projects={projects} preferred={missionProject?.projectId ?? lessonProject?.projectId ?? null}
                   onWindow={(w) => { if (w !== simWin.current) setSimReadyAt(null); simWin.current = w; }} onEdit={(id) => void handoff.open(id).catch(() => {})} editBusy={handoff.busy}
                   mirror={gatewayOn && gw ? <GatewayMirror group={gw.g} up={gw.up} down={gw.down} plaza={radio.status} />
                     : board.on ? <BoardMirror frame={board.live?.frame ?? null} /> : null} />
          {missionLesson && projects && !missionProject ? (
            <p className="pf-mb__hint" role="status" data-testid="fs-mb-mission-noproject">{t("student.microbit.missionNoProject", { lesson: missionLesson })}</p>
          ) : null}
          <div className={`pf-mb__status pf-mb__status--${connected ? "on" : "off"}`} data-testid="fs-mb-status">
            {connected ? t("student.microbit.connected") : live ? t("student.microbit.stale") : t("student.microbit.waiting")}
          </div>
          <p className="pf-mb__icon" data-testid="fs-mb-icon">{boardLabel || "—"}</p>
          <ul className="pf-mb__inputs" aria-label={t("student.microbit.inputs")}>
            <li data-on={!!frame?.a}>A</li>
            <li data-on={!!frame?.b}>B</li>
            <li data-on={!!frame?.logo}>{t("student.microbit.logo")}</li>
            <li data-on={!!frame?.shake}>{t("student.microbit.shake")}</li>
            <li data-on={!!toRemote(frame).dpad}>{t("student.microbit.tilt")} {toRemote(frame).dpad ?? ""}</li>
          </ul>
          <BoardPanel status={board.status} receiving={(board.on && board.at !== null && now - board.at < STALE_MS) || gatewayOn}
                      usb={board.usb} ble={board.ble} via={board.via}
                      onConnect={(how) => void board.connect(how)} onDisconnect={() => void board.disconnect()} />
        </section>

        <section className="pf-mb__play" aria-label={t("student.microbit.play")}>
          {stage3d && !stage3dFailed ? (
            <MbStage scene={scene} frame={frame} stars={stars} goals={starGoals}
                     template={templateOf({ scene, lessons, track, course, heart, sensors: sensorStates, plaza: radio.plaza, members: radio.members, targets: starTargets(scene, goalCfg) })}
                     decor={mbDecor.get(mbDecorKey(scene)) ?? cleanDefaultDecor(scene)} modelUrl={mbModelUrl}
                     ambience={ambienceOf(scene, sensorStates)}
                     pico={null} onFail={() => setStage3dFailed(true)} />
          ) : (
            <div className="pf-mb__stage" data-testid="fs-mb-stage">
              <span className="pf-mb__crowd" aria-hidden="true">{reactionOf(icon)} {reactionOf(icon)} {reactionOf(icon)}</span>
              <span className="pf-mb__jumps">{t("student.microbit.jumps", { n: play.jumps })}</span>
            </div>
          )}
          {!stage3dFailed && (
            <button type="button" className="pf-mb__stage-toggle" data-testid="fs-mb-stage3d-toggle"
                    onClick={() => { writeStage3d(!stage3d); setStage3d(!stage3d); }}>
              {t(stage3d ? "student.microbit.stage3d.off" : "student.microbit.stage3d.on")}
            </button>
          )}
          {pgGoals.length > 0 ? (
            <div className="pf-mb__mission" data-testid="fs-mb-mission">
              <h3>{t("student.microbit.mission.title")}</h3>
              <ul>
                {pgGoals.map((g) => (
                  <li key={g.key} data-state={g.state} data-testid={`fs-mb-mission-${g.key}`}>
                    {g.state === "verified" ? "✅" : "🎯"} {g.title}{" "}
                    <span className="pf-mb__mission-state">
                      {g.state === "verified" ? t("student.microbit.mission.verified") : sending ? t("student.microbit.mission.sending") : t("student.microbit.mission.open")}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="pf-mb__hint">{t("student.microbit.mission.hint")}</p>
            </div>
          ) : null}
          {/* 놀이 고르기 — 차시가 많아(20개 남짓) 단추 줄 대신 선택 목록 · 묶음: 공통 · 저학년(LE) · 입문 · 표준(L) · 라디오(2단계) */}
          <label className="pf-mb__scenes">
            <span className="pf-mb__scenes-label">{t("student.microbit.scene.pick")}</span>
            <select className="pf-mb__scene-select" value={scene} data-testid="fs-mb-pick"
                    onChange={(e) => { const sc = SCENES.find((x) => x.id === e.target.value); if (sc) setParams((p) => { const n = new URLSearchParams(p); n.set("play", sc.lesson ?? sc.id); return n; }, { replace: true }); }}>
              {SCENE_GROUPS.map((g) => (
                <optgroup key={g} label={t(`student.microbit.scene.group.${g}`)}>
                  {SCENES.filter((sc) => groupOf(sc) === g).map((sc) => (
                    <option key={sc.id} value={sc.id}>{sc.lesson ? `${sc.lesson} · ` : ""}{t(`student.microbit.scene.${sc.id}.name`)}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          {/* 왜 이 놀이인가 — 미션 장면(오늘의 미션이 바라는 차시)이 먼저, 아니면 내 작품의 차시 */}
          {!params.get("play") && missionLesson && sceneOf(missionLesson) ? (
            <p className="pf-mb__hint" data-testid="fs-mb-scene-auto">{t("student.microbit.scene.mission", { lesson: missionLesson })}</p>
          ) : autoScene && !params.get("play") ? (
            <p className="pf-mb__hint" data-testid="fs-mb-scene-auto">{t("student.microbit.scene.auto", { lesson: (lessonProject?.lessonId ?? "").replace(/^MB-/i, "") })}</p>
          ) : null}
          <p className="pf-mb__hint" data-testid="fs-mb-scene-how">{t(`student.microbit.scene.${scene}.how`)}</p>
          {scene === "faceCourse" ? <FaceCoursePanel s={course} />
            : scene === "track" ? <TrackPanel s={track} />
            : scene === "heartbeat" ? <HeartbeatPanel s={heart} level={frame ? litLevel(frame.leds) : 0} />
            : scene === "movie" ? <MoviePanel s={movie} leds={frame?.leds ?? null} />
            : scene === "greet" ? <GreetPanel s={lessons.greet} icon={icon} />
            : scene === "story" ? <StoryPanel s={lessons.story} />
            : scene === "harbor" ? <HarborPanel s={lessons.harbor} lit={!!frame && !isBlank(frame.leds)} />
            : scene === "gate" ? <GatePanel s={lessons.gate} />
            : scene === "maze" ? <MazePanel s={lessons.remotes.maze} />
            : scene === "balance" ? <BalancePanel s={lessons.remotes.balance} />
            : scene === "marathon" ? <MarathonPanel s={lessons.remotes.marathon} />
            : scene === "door" ? <DoorPanel s={lessons.remotes.door} />
            : scene === "watch" ? <WatchPanel s={lessons.remotes.watch} temp={frame?.sensors?.temp} />
            : scene === "cheer" ? <CheerPanel s={lessons.stages.cheer} />
            : scene === "showcase" ? <ShowcasePanel s={lessons.stages.showcase} />
            : scene === "melody" ? <MelodyPanel s={lessons.stages.melody} />
            : scene === "band" ? <BandPanel s={lessons.stages.band} />
            : scene === "hotCold" ? <HotColdPanel s={lessons.stages.hotCold} group={ai.radioGroup} />
            : scene === "boardGame" ? <BoardGamePanel s={lessons.dice} />
            : scene === "nameTag" ? <NameTagPanel s={lessons.scroll} icon={lessons.lastIcon} />
            : scene === "gallery" ? <GalleryPanel s={lessons.gallery} />
            : scene === "marquee" ? <MarqueePanel s={lessons.scroll} />
            : scene === "goals" ? <GoalsPanel s={lessons.goals} />
            : scene === "rps" ? <RpsPanel s={lessons.rps} />
            : scene === "reaction" ? <ReactionPanel s={lessons.reaction} lit={!!frame && !isBlank(frame.leds)} />
            : scene === "quiz" ? <QuizPanel s={lessons.quiz} />
            : scene === "dice" ? <DicePanel s={lessons.dice} />
            : scene === "ballMaze" ? <BallMazePanel s={lessons.ball} />
            : scene === "level" ? <LevelPanel s={lessons.level} tiltX={frame?.ax ?? 0} check={isLevelCheck(frame)} />
            : scene === "plaza" ? <RadioPanel status={radio.status} members={radio.members} plaza={radio.plaza} />
            : sensorScene ? <SensorPanel scene={sensorScene} s={sensorStates[sensorScene.id] ?? initialSensor()} sensors={frame?.sensors} boardLabel={boardLabel} />
            : scene === "aiCards" ? <AiCardsPanel programClasses={ai.classes} send={sendAi} boardLabel={boardLabel} su={density !== "se"}
                                                  quest={quest} questState={quest ? questStates[quest.id] ?? initialQuest() : undefined} cards={quest ? qCards : undefined}
                                                  onCard={onQuestCard} onPickQuest={pickQuest} temp={frame?.sensors?.temp} waking={waking}
                                                  partner={quest?.kind === "radio" ? partnerView(radio.plaza) : undefined} />
            : null}
          {scene === "stars" ? (<>
          <div className="pf-mb__grid" style={{ gridTemplateColumns: `repeat(${GRID}, 1fr)` }} data-testid="fs-mb-grid">
            {Array.from({ length: GRID * GRID }, (_, i) => {
              const x = i % GRID, y = Math.floor(i / GRID);
              const me = play.me.x === x && play.me.y === y;
              const star = play.star.x === x && play.star.y === y;
              return <span key={i} className="pf-mb__cell" data-me={me || undefined}>{me ? "🧒" : star ? "⭐" : ""}</span>;
            })}
          </div>
          <ol className="pf-mb__goals" data-testid="fs-mb-goals">
            <li data-done={goals.icons}>{goals.icons ? "✅" : "⬜"} {t("student.microbit.goal.icons", { n: Math.min(play.icons.length, ICONS_GOAL), goal: ICONS_GOAL })}</li>
            <li data-done={goals.buttons}>{goals.buttons ? "✅" : "⬜"} {t("student.microbit.goal.buttons")}</li>
            <li data-done={goals.stars}>{goals.stars ? "✅" : "⬜"} {t("student.microbit.goal.stars", { n: Math.min(play.stars, STARS_GOAL), goal: STARS_GOAL })}</li>
          </ol>
          </>) : null}
        </section>
      </div>
    </ScreenRoot>
  );
}
